import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button, Col, Row } from 'react-bootstrap';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import Icon from 'components/Icons/Icon';
import moment from 'moment';
import { isEqual } from 'lodash';
import TimezoneDate from 'components/Common/TimezoneDate';

import "./UserActionsChat.scss";
import Alert from 'components/Common/Alert';
import conxtdOut from 'apis/conxtdOut';
import { getSiteAssetViewUid } from 'services/redux/selectors/serviceDesk/assets';
import Loading from 'components/Loading';
import { SubmissionFailIcon, SubmissionSuccessIcon } from './SubmissionStatusIcon';
import { Tooltip } from 'components/Common/Tooltip';

// Per-type presentation. Message "type" separates the current user's own
// messages from other users and from system notifications. When this is wired
// to real data the incoming records only need a matching `type` field.
//   user_message  – the current user's messages (navy blue)
//   other_message – another user's messages (light grey)
//   notification  – non-user system events e.g. field changes (light grey)
const MESSAGE_STYLES = {
  user_message: {
    bubble: "#1b2a4a", // navy blue
    text: "#ffffff",
    iconBg: "#1b2a4a",
    icon: "fa-user",
  },
  other_message: {
    bubble: "#d9dce1", // light grey
    text: "#2c3e50",
    iconBg: "#8a94a6", // mid grey so the white glyph stays legible
    icon: "fa-user",
  },
  notification: {
    bubble: "#d9dce1", // light grey
    text: "#2c3e50",
    iconBg: "#8a94a6",
    icon: "fa-bell",
  },
};

// how often the log is automatically refreshed while the chat is open
const MESSAGE_REFRESH_MS = 60 * 1000; // 1 minute

function UserActionsChat({
  show,
  fullScreen,
  toggleFullScreen,
  showFullScreenToggle,
  lastUpdatedActionsLog,
  setLastUpdatedActionsLog,
  setUnreadCount,
  fetchLimit,
  setFetchLimit,
  setFetchButtonClickCounter,
  siteViewData,
}) {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [paneHeight, setPaneHeight] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const paneRef = useRef(null);
  const contentRef = useRef(null);
  const didAutoScrollRef = useRef(false);
  // whether the newest message was in view at the last scroll/measure. Starts
  // true so the chat opens pinned to the bottom while the layout settles.
  const wasAtBottomRef = useRef(true);
  // armed when a note is submitted successfully; consumed by the effect below
  // once the refreshed log has actually rendered
  const scrollAfterSubmitRef = useRef(false);
  const noteInputRef = useRef(null);
  // last siteViewData that was acted on, so the refresh below fires only on a genuine
  // change. null until the first populated value establishes the baseline.
  const prevSiteViewDataRef = useRef(null);
  // armed when an asset-data change triggers a refresh; consumed by the same
  // effect as scrollAfterSubmitRef once the refreshed log has rendered
  const scrollAfterDataChangeRef = useRef(false);
  // ids of messages already seen — used to spot new ones on each resync.
  // null until the first fetch establishes the baseline.
  const seenIdsRef = useRef(null);
  // live mirror of `show` so fetchMessages (memoised on assetUid) reads the
  // current value rather than a stale closure
  const showRef = useRef(show);
  const assetUid = useSelector(getSiteAssetViewUid);
  const currentUserId = useSelector(state => state.user.id);

  useEffect(() => {
    showRef.current = show;
  }, [show]);

  const defineErrorAlert = (type = "", field = "") => {
    let message = "";

    if (type === "id" && field) {
      message = `Could not retrieve the ${field} ID. Please try again later or contact support.`
    } else if (type === "format") {
      message = "Could not format submitted note correctly. Please try again later or contact support."
    } else if (type === "fetchMessages") {
      // message = "Could not retrieve log history. Please try again later or contact support."
      return;
    } else {
      message = "Could not submit note. Please try again later or contact support."
    }

    return Alert({
      text: message,
      icon: "error",
      timer: 5000,
    })
  }

  // retrieve user action logs history for this asset
  const [loadingSubmitNote, setLoadingSubmitNote] = useState(false);
  const [fetchingMessages, setFetchingMessages] = useState(false);
  const [submitSuccessfull, setSubmitSuccessfull] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [updateStatusColour, setUpdateStatusColour] = useState("lightgray");
  // bumped on each fetch / status change to re-trigger the pulse animation
  const [pulseTick, setPulseTick] = useState(0);

  useEffect(() => {
    if (fetchingMessages) {
      return setUpdateStatusColour("lightgray");
    }

    setUpdateStatusColour(updateStatus ? "green" : "red")
    return;
  }, [updateStatus, fetchingMessages])

  // re-play the status pulse whenever a fetch starts/finishes or the
  // update status changes
  useEffect(() => {
    setPulseTick((tick) => tick + 1);
  }, [fetchingMessages, updateStatus])

  const fetchMessages = useCallback((button = false) => {
    // early return in to stop excessive fetches to endpoint
    if (button && fetchLimit >= 1) {
      return;
    }
    // endpoint call
    if (!assetUid) { return };
    setFetchingMessages(true);
    const viewMessages = conxtdOut.get(`/company-assets-user-action-logs/view/asset/${assetUid}`);
    viewMessages.then((res) => {
      setFetchingMessages(false);
      setUpdateStatus(true);
      // record when the log was last successfully refreshed
      setLastUpdatedActionsLog(new moment());
      // mapping resulting data
      const data = res?.data?.logs;
      if (!data) { return };
      const mappedFormattedMessages = data.map(m => {
        const fields = m?.data?.fields ?? {};
        const firstName = m?.user?.first_name;
        const lastName = m?.user?.last_name;
        const author = firstName + " " + lastName;

        return {
          id: m.id,
          timestamp: m.created ?? null,
          text: m.action ?? "",
          type: m.type ?? "",
          fields: Object.entries(fields)?.map(([name, change]) => ({
            name,
            old: change?.old ?? null,
            new: change?.new ?? null,
          })) ?? "",
          author: author,
          author_first_name: m?.user?.first_name,
          author_last_name: m?.user?.last_name,
          author_initials: `${firstName[0] + lastName[0]}`,
          userId: m?.user?.id
        }
      })
        // oldest at the top, newest at the bottom of the chat
        .sort((a, b) => new moment(a.timestamp).valueOf() - new moment(b.timestamp).valueOf());

      // spot messages that weren't present on a previous resync
      if (seenIdsRef.current === null) {
        // first load for this asset — baseline only, nothing is "unread"
        seenIdsRef.current = new Set(mappedFormattedMessages.map((m) => m.id));
      } else {
        // new = not seen before, excluding the current user's own messages
        const newOnes = mappedFormattedMessages.filter(
          (m) => !seenIdsRef.current.has(m.id) && m.userId !== currentUserId
        );
        // mark every fetched message seen so none is reconsidered next resync
        mappedFormattedMessages.forEach((m) => seenIdsRef.current.add(m.id));
        // only count as unread when the chat is closed (i.e. not being read)
        if (newOnes.length > 0 && !showRef.current) {
          setUnreadCount((prev) => (prev || 0) + newOnes.length);
        }
      }

      setMessages(mappedFormattedMessages);
    }).catch((error) => {
      setFetchingMessages(false);
      setUpdateStatus(false);
      console.log('error fetching messages: ', error)
      defineErrorAlert("fetchMessages")
    })
  }, [assetUid, currentUserId, fetchLimit])

  // loads messages on initialisation (and resets the unread baseline for the
  // new asset so its existing notes aren't mistaken for unread)
  useEffect(() => {
    seenIdsRef.current = null;
    setUnreadCount(0);
    fetchMessages();
    // siteViewData deliberately omitted: it arrives just after mount, so
    // including it fired this a second time (and wiped the unread count) on
    // every data change. Refreshing on a real change is handled below.
  }, [assetUid, show])

  // Re-baseline the data-change detection below, keyed on the asset ALONE —
  // deliberately not on `show`. This used to live in the effect above, but that
  // also runs whenever the chat is opened or closed, which nulled the baseline
  // mid-session; the next genuine data change then looked like initialisation
  // and was swallowed without refreshing the log.
  useEffect(() => {
    prevSiteViewDataRef.current = null;
  }, [assetUid])

  // auto-refresh the log every minute while the asset view is open, so unread
  // notes are detected even when the chat panel itself is closed
  useEffect(() => {
    const intervalId = setInterval(fetchMessages, MESSAGE_REFRESH_MS);
    return () => clearInterval(intervalId);
  }, [fetchMessages])

  // the sticky pane must be exactly as tall as the visible area of the
  // #company-asset-page scroll container, whose height is now derived from
  // the page layout — track it (CSS alone can't read a scroll container's
  // client height, so the scss height is only a pre-measure fallback).
  //
  // Depend on `show`: the pane (and paneRef) only exist while the chat is
  // open, so this must (re)run when it opens — otherwise the observer is
  // never attached, paneHeight stays null, and the pane keeps the fallback
  // height, which rarely matches the flex-stretched side-pane button.
  useEffect(() => {
    if (!show) return;
    const scroller = paneRef.current?.closest("#company-asset-page");
    if (!scroller || typeof ResizeObserver === "undefined") return;
    const syncHeight = () => setPaneHeight(scroller.clientHeight);
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [show, messages]);

  // show the "jump to latest" button only while the messages overflow the
  // scroll area (overflow-y triggered) and the user has scrolled up from
  // the bottom
  const updateScrollState = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight - el.clientHeight > 1;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    setShowScrollDown(scrollable && distanceFromBottom > 8);
  }, [messages]);

  // Only genuine scroll activity updates the pin. It deliberately isn't set in
  // updateScrollState: that also runs from the layout effects, which fire
  // synchronously after a commit but *before* the resize observer — so a pane
  // resize would record "not at the bottom" (true only because the bottom had
  // just moved) and disarm the very re-pin meant to correct it.
  const handleContentScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    wasAtBottomRef.current = distanceFromBottom <= 8;
    updateScrollState();
  }, [updateScrollState]);

  const scrollToBottom = () => {
    const el = contentRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // recompute when the list changes (new content grows scrollHeight without
  // resizing the container) or when the pane height changes
  useEffect(() => {
    updateScrollState();
  }, [messages, paneHeight, updateScrollState]);

  // recompute when the scroll area itself is resized (fullscreen toggle etc.)
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      // A resize moves the bottom: the pane's measured height lands just after
      // the first open, and changes again whenever the viewport does (showing
      // or hiding the browser toolbar). The initial jump-to-newest had already
      // run and latched by then, so the view was left short of the bottom by
      // however much the pane changed. If the newest message was in view before
      // the resize, keep it there.
      if (wasAtBottomRef.current) el.scrollTop = el.scrollHeight;
      updateScrollState();
    });
    observer.observe(el);
    // the scroll container's own box only changes with the pane; watch the
    // messages wrapper too so late content growth (web fonts settling) is
    // caught as well
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [updateScrollState]);

  // each time the chat is opened, jump straight to the most recent message.
  // messages may load asynchronously, so this waits for them to be present
  // and only fires once per open (re-armed when the chat is closed).
  useEffect(() => {
    if (!show) {
      didAutoScrollRef.current = false;
      return;
    }
    if (didAutoScrollRef.current || messages.length === 0) return;
    const el = contentRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    updateScrollState();
    didAutoScrollRef.current = true;
  }, [show, messages, paneHeight, updateScrollState]);

  // grow the note box upwards with its content. The height is measured from
  // scrollHeight and applied inline; the css max-height caps it (and turns on
  // overflow) so the cap lives in one place. Resetting to "auto" first is what
  // lets it shrink again as text is deleted — otherwise scrollHeight can never
  // report less than the height already set.
  // Also re-runs on resize: changing the pane width rewraps the text, so a
  // height measured at the old width would be stale.
  useEffect(() => {
    const el = noteInputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [message, show, fullScreen, paneHeight]);

  // a submitted note, or a change to the asset's data, refetches the log
  // asynchronously — so the new entry only exists once that response lands and
  // React has committed it. This effect runs after that commit, so scrollHeight
  // includes the new entry and the jump lands on the real bottom.
  useEffect(() => {
    const afterSubmit = scrollAfterSubmitRef.current;
    const afterDataChange = scrollAfterDataChangeRef.current;
    if ((!afterSubmit && !afterDataChange) || messages.length === 0) return;
    // consume the request on the first log refresh afterwards, whatever it
    // contains, so a failed or empty refetch can't leave it armed and fire
    // unexpectedly on a later poll
    scrollAfterSubmitRef.current = false;
    scrollAfterDataChangeRef.current = false;

    const last = messages[messages.length - 1];
    const isOwn = last?.userId != null && last.userId === currentUserId;
    // only ever follow the user down to their own entry. After a data change
    // that means any type — an asset update ("UA") as well as a note ("N");
    // after a submit it's specifically the note that was just sent.
    const shouldScroll = afterDataChange ? isOwn : isOwn && last?.type === "N";
    if (shouldScroll) scrollToBottom();
  }, [messages, currentUserId]);

  const handleSubmit = () => {
    // ignore while a submit is in flight or a result icon is still showing
    if (loadingSubmitNote || submitSuccessfull !== null) return;
    const trimmed = message.trim();
    // validate BEFORE the loading flag — otherwise these early returns leave
    // loadingSubmitNote stuck true and the button shows the spinner forever
    // instead of the idle arrow
    if (!assetUid) return defineErrorAlert("id", "Asset");
    // if (!userDetails?.id) return defineErrorAlert("id", "User");
    if (!trimmed) return defineErrorAlert();
    setLoadingSubmitNote(true);
    const submitNote = conxtdOut.post(`/company-assets/add-note/${assetUid}`, {
      note: message,
    })

    submitNote.then(() => {
      setLoadingSubmitNote(false);
      setFetchingMessages(true);
      try {
        fetchMessages();
        setUpdateStatus(true);
        setSubmitSuccessfull(true);
        // don't scroll here: fetchMessages() is fire-and-forget, so at this
        // point the new note hasn't been fetched, setMessages hasn't run and
        // the DOM hasn't grown — scrollHeight is still the pre-submit value
        // and we'd stop short of the new message. Arm it instead and let the
        // effect below scroll once the refreshed log has rendered.
        scrollAfterSubmitRef.current = true;
      } catch {
        setFetchingMessages(false)
        setUpdateStatus(false);
      }

      setFetchingMessages(false);

      // Alert({
      //   text: "Submitted note successfully",
      //   icon: "success",
      //   timer: 5000,
      // })
    }).catch((error) => {
      setLoadingSubmitNote(false);
      setSubmitSuccessfull(false);
      console.log('error submitting note: ', error)
      return defineErrorAlert();
    })
    setMessage("");
  };

  const fieldTooltip = (old) => (
    <div>Changed from - <span className='font-weight-bold'>{old}</span></div>
  )

  // refresh the log when the asset's data actually changes, so field edits show
  // up in the timeline without waiting for the next poll.
  useEffect(() => {
    // ignore the empty value present before the asset data has loaded
    if (!siteViewData || Object.values(siteViewData).length === 0) return;

    // the first populated value is initialisation, not a change — record it as
    // the baseline and don't refetch (the mount effect has already loaded)
    if (prevSiteViewDataRef.current === null) {
      prevSiteViewDataRef.current = siteViewData;
      return;
    }

    // compare by value: siteViewData is a new object on each update, so an
    // identity check would refetch even when nothing actually changed
    if (isEqual(prevSiteViewDataRef.current, siteViewData)) return;

    prevSiteViewDataRef.current = siteViewData;
    // the refreshed log renders asynchronously (fetchMessages is
    // fire-and-forget), so arm the scroll rather than doing it here — the
    // effect above runs it once the new entries are on screen and the
    // scrollHeight includes them
    scrollAfterDataChangeRef.current = true;
    fetchMessages();
  }, [siteViewData, fetchMessages])

  return (
    !show ? <></>
      :
    <Col
      id="user-actions-chat"
      className="p-0"
      ref={paneRef}
      style={paneHeight ? { height: `${paneHeight}px` } : undefined}
    >
      {/* custom class — bootstrap's .container applies responsive
          max-widths + auto margins that create side gaps below 768px */}
      <div className="chat-container h-100 w-100 hide-scrollbar">
        {/* toolbar bar is always rendered (min-height in scss) so it stays
            visible at every width; the toggle button inside only shows where
            it's meaningful (not on the auto-full-width narrow widths) */}
        <Row className={`chat-toolbar px-3 ${showFullScreenToggle ? 'justify-content-between' : 'justify-content-start'}`}>
          
          {/* Last Updated */}
          <Col className='d-flex justify-content-start align-items-center p-0 chat-last-updated'>
            {/* key bumps with pulseTick so the animation replays on each update */}
            <span key={pulseTick} className='chat-status-indicator mr-2'>
              <Tooltip
                description={updateStatus ? "Connected" : "Disconnected"}
                placement='top'
              >
                <Icon
                  className='fas fa-circle fa-xs'
                  style={{ color: updateStatusColour }}
                />
              </Tooltip>
            </span>

            <div className='d-flex align-items-center flex-row flex-nowrap'>
              <TimezoneDate
                date={lastUpdatedActionsLog}
                timezone="Europe/London"
                hideTimezone
                descriptionBeforeTimezone="Last Updated: "
              />
            </div>
          </Col>
          
          {/* Full Screen Button */}
          <Col className='d-flex justify-content-end p-0'>
            <Button
              variant="light"
              size="sm"
              className="mr-1"
              title="Sync Notes"
              onClick={() => {
                // used to detect click events on the button
                setFetchButtonClickCounter(count => count + 1);
                setFetchLimit(prev => {
                  if (prev === 2) {
                    return prev;
                  }
                  return prev + 1;
                })
                return fetchMessages(true);
              }}
            >
              <Icon className='fas fa-sync'/>
            </Button>

            {showFullScreenToggle &&
              <Button
                variant="light"
                size="sm"
                className="fullscreen-toggle"
                title={fullScreen ? "Exit full screen" : "Full screen"}
                onClick={toggleFullScreen}
              >
                <Icon className={`fas ${fullScreen ? "fa-compress" : "fa-expand"}`} />
              </Button>
            }
          </Col>

        </Row>
        <div className="content" ref={contentRef} onScroll={handleContentScroll}>
          {messages?.length <= 0 ?
            <div className="d-flex flex-wrap flex-column">
              <Row className="d-flex justify-content-between align-items-center flex-row flex-nowrap w-100 pb-2 mt-3">
                <Col className="d-flex justify-content-start align-items-center"><h6 className='m-0 p-0' style={{ whiteSpace: "nowrap" }}>Nothing to see here yet</h6></Col>
                <Col>
                  <Icon
                    className="fas fa-book d-flex justify-content-end align-items-center m-0 p-0"
                    style={{ fontSize: "1.4em" }}
                  />
                </Col>
              </Row>
              <span className='d-flex w-100 justify-content-center'>
                <span className='d-flex justify-content-center flex-nowrap' style={{ borderBottom: "1px solid lightgrey", width: "100%" }}></span>
              </span>
              <p className='mt-2'>Make a change to this asset or add a note below to get started...</p>
            </div>
          : 
            <VerticalTimeline layout="1-column" className="py-0 px-2">
              {messages.map((msg, index) => {
                  const style = MESSAGE_STYLES[msg.type] || MESSAGE_STYLES.notification;
                  // the current user's own messages mirror to the right
                  const isOwn = msg.userId != null && msg.userId === currentUserId;

                  const eventMoment = new moment(msg.timestamp);
                  const yesterday = new moment()
                    .startOf("day")
                    .isAfter(eventMoment);

                  const beforeYesterday = new moment()
                    .subtract(1, "days")
                    .startOf("day")
                    .isAfter(eventMoment);

                  const format = beforeYesterday
                    ? "dddd MMMM Do, YYYY HH:mm"
                    : `[${yesterday ? "Yesterday at" : "Today at"}] HH:mm`;

                  return (
                    <div className='d-flex flex-column flex-wrap chat-timeline-row' key={msg.id ?? index}>
                      <VerticalTimelineElement
                        className={`vertical-timeline-element--work pb-0 mb-0 chat-timeline-element chat-message chat-message--${msg.type}`}
                        contentStyle={{
                          background: 'white',
                          color: 'black',
                        }}
                        contentArrowStyle={{
                          borderRight: "7px solid " + style.bubble,
                        }}
                        iconStyle={{ background: !isOwn ? '#243A62' : style.iconBg }}
                        icon={
                          <Tooltip
                            // built from whichever name parts exist: a blank
                            // description renders an empty tooltip box, which
                            // reads as the tooltip not appearing at all
                            description={
                              [msg?.author_first_name, msg?.author_last_name]
                                .filter(Boolean)
                                .join(" ") || "Unknown user"
                            }
                            placement='top'
                          >
                            {/* the trigger fills the circular badge, so the
                                tooltip is measured against the icon container
                                and sits above it — anchoring it to the glyph
                                inside instead placed it over the icon */}
                            <span className="chat-message-icon-target">
                              {msg?.type === 'N' ?
                                <span style={{ color: 'white', fontSize: '1.2em' }}>{msg?.author_initials}</span>
                              :
                                <Icon className={`fas fa-user-edit`} style={{ color: "#ffffff", fontSize: "1.4em" }} />
                              }
                            </span>
                          </Tooltip>
                        }
                      >
                        <div className={`chat-message-${msg?.type === 'N' ? 'text' : 'author font-weight-bold'}`}>
                          {msg?.text}
                        </div>
                        {!msg?.fields || msg?.fields?.length <= 0 ?
                          <></>
                        :
                          <div className="chat-message-text mt-1">
                            {msg?.type === 'N' ?
                              msg?.text
                            :
                              <ul id='msg-fields-list' className='d-flex flex-column flex-wrap'>
                                {
                                  msg.fields?.map(f => {
                                    if (!f) { return null }

                                    if (!f?.old) {
                                      return (
                                        <li className='msg-field-list-item p-0 m-0'>
                                          <span className='mr-1'>{f?.name}: {f?.new}</span>
                                        </li>
                                      )
                                    }

                                    return (
                                      <div>
                                        <Tooltip
                                          description={fieldTooltip(f?.old)}
                                          placement='top'
                                        >
                                          <li className='msg-field-list-item'>
                                            <span className='mr-1'>{f?.name}: {f?.new}</span>
                                          </li>
                                        </Tooltip>
                                      </div>
                                    )
                                  })
                                }
                              </ul>
                            }
                          </div>
                        }
                      </VerticalTimelineElement>
                      {/* Timeline footer */}
                      <div className="timeline-footer mt-1 d-flex justify-content-end">
                        <div className="timeline-date-footer">
                          <TimezoneDate
                            date={eventMoment}
                            timezone="Europe/London"
                            hideTimezone
                            localFormat={format}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
            </VerticalTimeline>
          }
        </div>
        {/* floats over the bottom of the messages, above the input box —
            only while the messages overflow and the user has scrolled up */}
        <div className="scroll-to-bottom-wrap">
          <Button
            variant="light"
            className={`scroll-to-bottom-btn ${showScrollDown ? '--show' : '--hide'}`}
            onClick={scrollToBottom}
            title="Jump to latest messages"
            aria-label="Jump to latest messages"
          >
            <Icon className="fas fa-angle-double-down" />
          </Button>
        </div>
        
        <div className="chat-box">
          <textarea
            ref={noteInputRef}
            rows={1}
            className="chat-box-input"
            placeholder="Add a note..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            // Enter still submits; Shift+Enter inserts a newline. preventDefault
            // stops the newline being typed into the box as well as submitting
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <Button
            variant="primary"
            className="chat-submit-btn"
            onClick={handleSubmit}
            // disabled while a result icon is showing
            disabled={submitSuccessfull !== null}
          >
            {loadingSubmitNote ?
                <Loading />
              : submitSuccessfull === true ?
                  <SubmissionSuccessIcon onComplete={() => setSubmitSuccessfull(null)} />
              : submitSuccessfull === false ?
                  <SubmissionFailIcon onComplete={() => setSubmitSuccessfull(null)} />
              :
                  <Icon className="fas fa-arrow-right" />
            }
          </Button>
        </div>
      </div>
    </Col>
  )
}

export default UserActionsChat
